import { Link } from "react-router-dom";
import { UserAvatar } from "@/components/UserAvatar";
import { cn } from "@/lib/utils";
import { usePeopleBoard } from "@/hooks/usePeopleBoard";

/** People currently over the 1 focus + 2 queue rule of thumb, worst first. */
export function LoadLimitSection() {
  const { people, isLoading, isError } = usePeopleBoard();

  if (isLoading) {
    return <div className="h-40 animate-pulse rounded-xl border border-border bg-muted/30" />;
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center text-sm text-destructive">
        Failed to load workload.
      </div>
    );
  }

  const overLimit = people
    .filter((p) => p.focus.length > 1 || p.queue.length > 2)
    .sort((a, b) => b.focus.length - a.focus.length || b.queue.length - a.queue.length);

  if (overLimit.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 bg-muted/20 p-8 text-center">
        <p className="text-sm font-medium text-muted-foreground">Everyone is within 1 focus + 2 queue.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {overLimit.map((p) => {
        const focusOver = p.focus.length > 1;
        const queueOver = p.queue.length > 2;
        return (
          <div key={p.user.pid} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <UserAvatar src={p.user.avatarUrl} name={p.user.fullName} className="h-9 w-9" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{p.user.fullName}</p>
                <p className="text-xs text-muted-foreground">
                  <span className={cn(focusOver && "font-medium text-amber-600 dark:text-amber-500")}>{p.focus.length} focus</span>
                  {" · "}
                  <span className={cn(queueOver && "font-medium text-amber-600 dark:text-amber-500")}>{p.queue.length} queue</span>
                </p>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[...p.focus, ...p.queue].map((e) => (
                <Link
                  key={e.priority.pid}
                  to={`/priorities/${e.priority.pid}`}
                  className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {e.priority.code}
                </Link>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
